import { arrayRange, EmptyArray, groupBy, max, sortBy, uniqueBy } from 'lib/array/arrayUtils'
import {
  PACKAGE_UPGRADE_VALUES,
  TOUR_V2_FEMALE_TRAVELLER_TITLES,
  TOUR_V2_MALE_TRAVELLER_TITLES,
  getOfferPackageUpgradeDisplayTextMap,
  ALL_TOUR_PACKAGE_TYPE,
  TOUR_HERO_IMAGES_IDS_ROTATION,
  TOUR_V2_PACKAGE_OPTION_BASE,
  TOUR_V2_PACKAGE_OPTION_EXTENDED,
  TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE,
  tourOptionPackages,
} from 'constants/tours'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import switchFunc from 'lib/function/switchFunc'
import { addDays, isSameDay } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import config from 'constants/config'
import type { IntlShape } from 'react-intl'

export function deduceGenderFromTitle(title: App.Title): App.Gender {
  if (TOUR_V2_FEMALE_TRAVELLER_TITLES.includes(title)) {
    return 'Female'
  }

  if (TOUR_V2_MALE_TRAVELLER_TITLES.includes(title)) {
    return 'Male'
  }

  return 'Other'
}

export function depositAmountStringBuilder(
  defaultDepositAmountPercentage: number,
  depositType: App.Tours.V2OfferDepositType | undefined,
  depositAmount: number | undefined,
  intl: IntlShape,
) {
  const currencySymbol = getDepositCurrencySymbol(intl)
  const baseText = intl.formatMessage({
    id: 'PhW9jrSh3E',
    defaultMessage: 'Secure with a',
    description: 'Base text prefix used before deposit amount label for tours',
  })

  if (depositType === 'flat') {
    return {
      full: intl.formatMessage(
        {
          id: 'plwo8+79Ww',
          defaultMessage: '{baseText} {currencySymbol}{depositAmount}/person deposit',
          description: 'Tour deposit description for a flat amount per person',
        },
        {
          baseText,
          currencySymbol,
          depositAmount,
        },
      ),
      short: intl.formatMessage(
        {
          id: 'FJu1ZWbIVG',
          defaultMessage: '{currencySymbol}{depositAmount}/person',
          description: 'Short tour deposit label for a flat amount per person',
        },
        {
          currencySymbol,
          depositAmount,
        },
      ),
    }
  }

  if (depositType === 'percentage') {
    return {
      full: intl.formatMessage(
        {
          id: 'UhLgBEADJ3',
          defaultMessage: '{baseText} {depositAmount}% deposit',
          description: 'Tour deposit description for a percentage deposit amount',
        },
        {
          baseText,
          depositAmount,
        },
      ),
      short: intl.formatMessage(
        {
          id: 'PDlmxRdqzF',
          defaultMessage: '{depositAmount}%',
          description: 'Short tour deposit label for a percentage amount in the tours section',
        },
        { depositAmount },
      ),
    }
  }

  return {
    full: intl.formatMessage(
      {
        id: '/9lY0x6J+U',
        defaultMessage: '{baseText} {defaultDepositAmountPercentage}% deposit',
        description: 'Tour deposit description using default percentage amount',
      },
      {
        baseText,
        defaultDepositAmountPercentage,
      },
    ),
    short: intl.formatMessage(
      {
        id: 'Dc5//sRjL0',
        defaultMessage: '{defaultDepositAmountPercentage}%',
        description: 'Short tour deposit label using default percentage amount',
      },
      { defaultDepositAmountPercentage },
    ),
  }
}

function getDepositCurrencySymbol(intl: IntlShape): string {
  const locale = intl.locale || 'en'
  const localeRegionCode = locale.split('-')[1]?.toUpperCase()
  const regionCode = config.regions.find(region => region.code === localeRegionCode)?.code ?? config.defaultRegion
  const currencyCode = config.regions.find(region => region.code === regionCode)?.currencyCode ?? 'USD'

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    return formatter.formatToParts(0).find(part => part.type === 'currency')?.value ?? currencyCode
  } catch {
    return currencyCode
  }
}

const roomTypeUpgrade = new Set([
  'twinShare',
  'tripleShare',
  'quadShare',
  'multiShare',
])
export function hasRoomTypeUpgrade(roomType: App.Tours.V2OfferRoomType | undefined) {
  return roomTypeUpgrade.has(roomType ?? '')
}

const roomTypePackageUpgrade = new Set([
  'single_regular',
  'single_vip',
  'single_platinum',
  'twin_regular',
  'twin_vip',
  'twin_platinum',
])
export function hasRoomTypePackageUpgrade(roomType: App.Tours.V2OfferRoomType | undefined) {
  return roomTypePackageUpgrade.has(roomType ?? '')
}

// Basically if the tag is "Offer ends in X days" it should be critical variant, otherwise positive variant
export function getUrgencyTagVariant(urgencyTag: string) {
  if (urgencyTag.toLocaleLowerCase().includes('ends in')) {
    return 'critical2'
  }
  return 'positive'
}

export function getTourV2VariationDuration(variation: App.Tours.TourV2OfferVariation) {
  const lastDay = max(variation.itinerary ?? [], (item) => item.startDay)! // guaranteed to exist
  const daysCount = lastDay.startDay + lastDay.duration - 1

  return daysCount
}

export function getTourPricePerDay(pricePerPerson: number, memberPricePerPerson: number, durationInDays?: number) {
  if (durationInDays === undefined || durationInDays <= 0) return
  return { pricePerPersonPerDay: Math.round(pricePerPerson / durationInDays), memberPricePerPersonPerDay: Math.round(memberPricePerPerson / durationInDays) }
}

export function getTourValueAtPerDay(valuePerPerson?: number, durationInDays?: number) {
  if (!durationInDays || !valuePerPerson) return
  return Math.round(valuePerPerson / durationInDays)
}

export function getRoomTypeDisplayText(roomType: string, offerId: string, packageUpgradeOptionDisplayName?: string) {
  const [room, packageOption] = roomType.split('_')
  if (packageUpgradeOptionDisplayName) {
    return `${room} ${packageUpgradeOptionDisplayName.toLocaleLowerCase()}`
  }
  return packageOption ? `${room} ${getOfferPackageUpgradeDisplayTextMap(offerId).get(packageOption as PACKAGE_UPGRADE_VALUES)?.toLocaleLowerCase()}` : roomType
}

const TOUR_V2_ROOM_TYPE_CAPACITIES: Record<App.Tours.V2OfferRoomType, number> = {
  single: 1,
  twin: 2,
  twinShare: 1,
  triple: 3,
  tripleShare: 1,
  quad: 4,
  quadShare: 1,
  multiShare: 1,
  single_regular: 1,
  single_vip: 1,
  single_platinum: 1,
  twin_regular: 2,
  twin_vip: 2,
  twin_platinum: 2,
}
const roomTypeCapacitySwitchFunc = switchFunc(TOUR_V2_ROOM_TYPE_CAPACITIES, 1)
export function getRoomTypeCapacity(roomType: App.Tours.V2OfferRoomType | undefined): number {
  return roomTypeCapacitySwitchFunc(roomType ?? '')
}

const TOUR_V2_ROOM_TYPE: Partial<Record<App.Tours.V2OfferRoomType, string>> = {
  single_regular: 'regular',
  single_vip: 'vip',
  single_platinum: 'platinum',
  twin_regular: 'regular',
  twin_vip: 'vip',
  twin_platinum: 'platinum',
}
export const getPackageOptionType = switchFunc(TOUR_V2_ROOM_TYPE, '')

export function getPackageOptionDisplayText(packageOption: string, offerId: string) {
  const lowerCasedPackageOption = packageOption.toLowerCase()

  return getOfferPackageUpgradeDisplayTextMap(offerId).get(lowerCasedPackageOption as PACKAGE_UPGRADE_VALUES) ?? packageOption
}

export function getTourPackageListForm(
  departures: Array<App.Tours.TourV2OfferDeparture>,
  tourId: string,
  intl: IntlShape,
): Array<{ type: string, name: string }> {
  const optionsAvailables = departures.flatMap(departure => departure.options).map(option => ({
    type: option.packageType!,
    name: getPackageOptionDisplayText(option.packageType!, tourId),
  }))
  const uniquePackageTypes = uniqueBy(optionsAvailables, (option) => option.type)
  const allPackageType = {
    type: ALL_TOUR_PACKAGE_TYPE,
    name: intl.formatMessage({
      id: 'wRo0YXAd+Q',
      defaultMessage: 'All packages',
      description: 'Filter option label to show all tour package types',
    }),
  }
  return [allPackageType, ...uniquePackageTypes]
}

export function findCheapestTourV2Variation(offer: App.Tours.TourV2Offer): App.Tours.TourV2OfferVariation | undefined {
  const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
  const cheapestDeparture = cheapestPurchasableOption && offer.departures[cheapestPurchasableOption.fkDepartureId]
  const cheapestVariation = cheapestDeparture && offer.variations[cheapestDeparture.fkVariationId]

  return cheapestVariation
}

const TOUR_V2_ROOM_TYPE_COPY: Record<App.Tours.V2OfferRoomType, string> = {
  single: 'Single room',
  twin: 'Twin room',
  twinShare: 'Twin share',
  triple: 'Triple room',
  tripleShare: 'Triple share',
  quad: 'Quad room',
  quadShare: 'Quad share',
  multiShare: 'Multi share',
  single_regular: 'Single room',
  single_vip: 'Single room',
  single_platinum: 'Single room',
  twin_regular: 'Twin room',
  twin_vip: 'Twin room',
  twin_platinum: 'Twin room',
}

export const formatRoomType = switchFunc(TOUR_V2_ROOM_TYPE_COPY, undefined)

export const formatPackageOptionType = (tourId: string) => {
  const PACKAGE_UPGRADE_TEXT = getOfferPackageUpgradeDisplayTextMap(tourId)
  const TOUR_V2_UPGRADE_ROOM: Partial<Record<App.Tours.V2OfferRoomType, string>> = {
    single_regular: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.REGULAR),
    twin_regular: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.REGULAR),
    single_vip: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.VIP),
    twin_vip: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.VIP),
    single_platinum: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.PLATINUM),
    twin_platinum: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.PLATINUM),
  }
  return switchFunc(TOUR_V2_UPGRADE_ROOM, '')
}

export interface TourAccommodationBookingStatus {
  preAccommodationBooked: boolean
  postAccommodationBooked: boolean
}

interface CheapestOfTourV2Offer {
  cheapestDeparture?: App.Tours.TourV2OfferDeparture
  cheapestPurchasableOption?: App.Tours.TourV2OfferPurchasableOption
  cheapestVariation?: App.Tours.TourV2OfferVariation
  hasSomeDiscounts?: boolean
  isDiscountOnTour?: boolean
}
export function findCheapestOfTourV2Offer(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  variationId?: App.Tours.TourV2OfferVariation['id'],
  isLuxPlusToursEnabled = false,
): CheapestOfTourV2Offer {
  const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer, variationId, isLuxPlusToursEnabled)

  if (!cheapestPurchasableOption) {
    return {
      cheapestDeparture: undefined,
      cheapestPurchasableOption: undefined,
      cheapestVariation: undefined,
      hasSomeDiscounts: false,
      isDiscountOnTour: false,
    }
  }

  const cheapestDeparture = offer.departures[cheapestPurchasableOption.fkDepartureId]
  const cheapestVariation = offer.variations[cheapestDeparture.fkVariationId]

  const discountOnPurchasableOption = offer.purchasableOptions.filter((purchasableOption) => {
    return purchasableOption.fullPrice - purchasableOption.price > 0
  })
  const hasSomeDiscounts = discountOnPurchasableOption.length > 0 && offer.purchasableOptions.length !== discountOnPurchasableOption.length
  const isDiscountOnTour = discountOnPurchasableOption.length === offer.purchasableOptions.length

  return {
    cheapestDeparture,
    cheapestPurchasableOption,
    cheapestVariation,
    hasSomeDiscounts,
    isDiscountOnTour,
  }
}

export function findCheapestTourExperience(purchasableExperienceOptions: Array<App.Tours.TourV2OfferPurchasableOption>): App.Tours.TourV2OfferPurchasableOption | undefined {
  const cheapestPurchasableExperienceOptions = sortBy(purchasableExperienceOptions, (purchasableExperienceOption) => purchasableExperienceOption.price, 'asc')
  return cheapestPurchasableExperienceOptions.length > 0 ? cheapestPurchasableExperienceOptions[0] : undefined
}

export interface TourDiscount {
  hasSomeDiscount: boolean
  allIsDiscounted: boolean
}
export function findDiscountOnTour(offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary, variationId?: string, departureIds?: Array<string>): TourDiscount {
  let purchasableOptions = offer.purchasableOptions
  if (variationId) {
    purchasableOptions = purchasableOptions.filter(purchaseOption => purchaseOption.fkVariationId === variationId)
  } else if (departureIds) {
    purchasableOptions = purchasableOptions.filter(purchaseOption => departureIds.includes(purchaseOption.fkDepartureId))
  }
  const discountOnPurchasableOption = purchasableOptions.filter((purchasableOption) => {
    return purchasableOption.fullPrice - purchasableOption.price > 0
  })
  const hasSomeDiscount = discountOnPurchasableOption.length > 0 && purchasableOptions.length !== discountOnPurchasableOption.length
  const allIsDiscounted = discountOnPurchasableOption.length === purchasableOptions.length

  return {
    hasSomeDiscount,
    allIsDiscounted,
  }
}

export type LuxPlusLabelTagOptions = 'all' | 'price-only' | 'access-only' | 'none'

export function getTourTags(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  isLuxPlusToursAvailable?: boolean,
  luxPlusLabels: LuxPlusLabelTagOptions = 'all',
  departureId?: string,
): Array<App.OfferUrgencyLabel> {
  if (departureId) {
    const departure = offer.departures[departureId]
    return departure.urgencyTags
  }
  const { hasSomeDiscount, allIsDiscounted } = findDiscountOnTour(offer)

  const tags: Array<App.OfferUrgencyLabel> = []

  if (allIsDiscounted) {
    tags.push({ type: 'sale' })
  } else if (hasSomeDiscount) {
    tags.push({ type: 'departuresale' })
  }

  if (isLuxPlusToursAvailable) {
    if (
      offer.luxPlus.access === 'earlyAccess' &&
      offer.schedule &&
      (luxPlusLabels === 'all' || luxPlusLabels === 'access-only')
    ) {
      tags.push({ type: 'lux_plus_early_access' })
      if (new Date(offer.schedule.start) > new Date()) {
        tags.push({ type: 'lux_plus_general_release', end: offer.schedule.start })
      }
    }
  }

  if (offer.isAgentHubExclusive) {
    tags.push({ type: 'agenthub_exclusive' })
  }
  return [...tags, ...offer.urgencyTags]
}

export interface TourV2PricePerDayDetails {
  isDefaultPriceView: boolean
}

export function checkPurchasableOptionHasPackageUpgradeOrRoomType(
  purchasableOptions: Array<App.Tours.TourV2OfferPurchasableOption>,
): { hasPackageUpgrade: boolean, hasRoomUpgrade: boolean } {
  const hasPackageUpgrade = groupBy(purchasableOptions, purchasableOption => purchasableOption.packageType).size > 1
  const hasRoomUpgrade = purchasableOptions.some(purchasableOption => hasRoomTypeUpgrade(purchasableOption.roomType))
  return { hasPackageUpgrade, hasRoomUpgrade }
}

export function checkInventoryAvailable(roomInventory: Map<string, number>, singleRoomsCount: number, twinRoomsCount: number, packageOption: string): boolean {
  const singleRoomInventory = roomInventory.get(`single_${packageOption.toLowerCase()}`) ?? 0
  const twinRoomInventory = roomInventory.get(`twin_${packageOption.toLowerCase()}`) ?? 0
  return singleRoomInventory >= singleRoomsCount && twinRoomInventory >= twinRoomsCount * 2
}

function getDateStringRange(startDate: Date, endDate: Date) {
  if (isSameDay(startDate, endDate)) return undefined
  const firstDate = startDate.getMonth() === endDate.getMonth() ?
      startDate.toLocaleString('en-AU', { day: '2-digit' }) :
      startDate.toLocaleString('en-AU', { day: '2-digit', month: 'short' })
  return `${firstDate} - ${endDate.toLocaleString('en-AU', { day: '2-digit', month: 'short' })}`
}

function consolidateEmptyTourExperience(
  activityDay: App.Tours.TourExperienceByDay,
  endDay: number,
  locations: Array<string>,
  itineraryEndDate: Date,
  intl: IntlShape,
) {
  const rangeDay = [activityDay.day, endDay]
  const locationsConsolidate = [...activityDay.locations, ...locations]
  activityDay.title = intl.formatMessage(
    {
      id: 'AXk56HTAaJ',
      defaultMessage: 'Day {dayRange}: {locations}',
      description: 'Itinerary title for a day range with locations for tours',
    },
    {
      dayRange: rangeDay.join(' - '),
      locations: locationsConsolidate.join(' - '),
    },
  )
  activityDay.rangeDay = rangeDay
  activityDay.rangeDate = getDateStringRange(activityDay.date, itineraryEndDate)
  activityDay.locations = locationsConsolidate
  return activityDay
}

export function getTourExperiencesByItineraryDay(
  tour: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | undefined,
  variationId: string,
  departureId: string,
  intl: IntlShape,
): Array<App.Tours.TourExperienceByDay> {
  if (!tour) {
    return []
  }
  const departure = tour.departures[departureId]
  if (!departure) {
    return []
  }
  const itineraries = variationId ? tour.variations[variationId].itinerary : []
  let indexToKeepForNoExperiences = 0
  return itineraries.reduce<Array<App.Tours.TourExperienceByDay>>((acc, itineraryItem) => {
    const itineraryId = itineraryItem.id
    const departureDate = new Date(departure.startDate)
    const itineraryDate = addDays(departureDate, itineraryItem.startDay - 1)
    const itineraryEndDate = addDays(itineraryDate, itineraryItem.duration - 1)
    const endDay = itineraryItem.startDay + itineraryItem.duration - 1
    const rangeDay = itineraryItem.duration > 1 ? [itineraryItem.startDay, endDay] : undefined
    const rangeDate = getDateStringRange(itineraryDate, itineraryEndDate) ?? undefined
    const locations = itineraryItem.locationsVisited
    const titleConsolidate = intl.formatMessage(
      {
        id: 'mwxhYMkBeh',
        defaultMessage: 'Day {dayOrRange}: {locations}',
        description: 'Itinerary title for day or day range with locations',
      },
      {
        dayOrRange: rangeDay?.join(' - ') ?? itineraryItem.startDay,
        locations: itineraryItem.locationsVisited.join(' - '),
      },
    )
    if (itineraryItem.experiences.length > 0) {
      // only skip going to next index if we're the first itinerary with experiences
      if ((indexToKeepForNoExperiences === 0 && acc[indexToKeepForNoExperiences]) || indexToKeepForNoExperiences > 0) indexToKeepForNoExperiences++
      if (rangeDay) {
        const [startDay, endDay] = rangeDay
        arrayRange(startDay, endDay).forEach(day => {
          const experiencesByDay = itineraryItem.experiences.filter(experience => experience.dayNumbers.includes(day))
          const date = addDays(departureDate, day - 1)
          const title = intl.formatMessage(
            {
              id: 'pEC1uoG1Be',
              defaultMessage: 'Day {day}: {locations}',
              description: 'Itinerary title for a single day with locations in the tours section',
            },
            {
              day,
              locations: itineraryItem.locationsVisited.join(' - '),
            },
          )
          if (experiencesByDay.length > 0) {
            acc[indexToKeepForNoExperiences] = { title, day, date, locations, experiences: experiencesByDay, itineraryId }
            indexToKeepForNoExperiences++
          } else {
            // keep index for no experiences on consecutive day
            if (acc[indexToKeepForNoExperiences]) {
              const experienceDay = acc[indexToKeepForNoExperiences]
              acc[indexToKeepForNoExperiences] = consolidateEmptyTourExperience(experienceDay, endDay, locations, itineraryEndDate, intl)
            } else {
              acc[indexToKeepForNoExperiences] = { title, day, date, locations, itineraryId }
            }
          }
        })
      } else {
        acc[indexToKeepForNoExperiences] = {
          title: titleConsolidate,
          day: itineraryItem.startDay,
          date: itineraryDate,
          experiences: itineraryItem.experiences,
          locations,
          itineraryId,
        }
        indexToKeepForNoExperiences++
      }
    } else {
      // keep index for no experiences on consecutive day
      if (acc[indexToKeepForNoExperiences]) {
        const experienceDay = acc[indexToKeepForNoExperiences]
        acc[indexToKeepForNoExperiences] = consolidateEmptyTourExperience(experienceDay, endDay, locations, itineraryEndDate, intl)
      } else {
        acc[indexToKeepForNoExperiences] = {
          itineraryId,
          title: titleConsolidate,
          day: itineraryItem.startDay,
          date: itineraryDate,
          locations,
          rangeDay,
          rangeDate,
        }
      }
    }
    return acc
  }, [])
}

export function checkTourExperienceTimeSlot(
  tourExperienceItems: Array<App.Checkout.TourV2ExperienceItem>,
  timeSlots: Array<string>,
  tourExperienceDate: Date,
) {
  const dateTourExperience = moment(tourExperienceDate).format(ISO_DATE_FORMAT)
  if (timeSlots.length === 0) return true
  return timeSlots.some((timeSlot) => {
    const [candidateStart, candidateEnd] = timeSlot.split(' - ')
    const tourExperienceStartDate = Date.parse(`${dateTourExperience}T${candidateStart}`)
    const tourExperienceEndDate = Date.parse(`${dateTourExperience}T${candidateEnd}`)
    return tourExperienceItems.every((tourExperienceItem) => {
      if (!tourExperienceItem?.purchasableOption?.timeSlot) return true
      const [existingStart, existingEnd] = tourExperienceItem?.purchasableOption?.timeSlot?.split(' - ') ?? []
      const startDate = Date.parse(`${tourExperienceItem.date}T${existingStart}`)
      const closeDate = Date.parse(`${tourExperienceItem.date}T${existingEnd}`)
      return !(tourExperienceStartDate >= startDate && tourExperienceStartDate <= closeDate) &&
        !(tourExperienceEndDate >= startDate && tourExperienceEndDate <= closeDate) &&
        !(tourExperienceStartDate <= closeDate && tourExperienceEndDate >= startDate) &&
        !(tourExperienceStartDate <= startDate && tourExperienceEndDate >= closeDate)
    })
  })
}

export function getTourV2SavingsWithLuxPlus(purchasableOption: App.Tours.TourV2OfferPurchasableOption | undefined): number | undefined {
  const { price: pricePerPerson, memberPrice } = purchasableOption ?? {}
  const savingsWithLuxPlus = (!pricePerPerson || !memberPrice) ? undefined : Number(pricePerPerson.toFixed()) - Number(memberPrice.toFixed())
  return savingsWithLuxPlus
}

export function getTourItemKey(tourItem: App.Checkout.TourV2Item): string {
  return tourItem.privateRequestKey ? `${tourItem.offerId}-${tourItem.privateRequestKey}` : tourItem.offerId
}

export function isCurrentDepartureEligibleForDateChange(tour: App.Tours.TourV2Offer, tourOrderItem: App.Tours.TourV2OrderItem): boolean {
  const handbackDate = tour.departures[tourOrderItem.departureId]?.handbackDate
  return !!handbackDate && new Date(handbackDate) > new Date()
}

export function isTourProductTypeStyled(productType: App.Tours.V2ProductType): productType is App.Tours.V2ProductTypeStyled {
  return productType === 'deluxe_tour' || productType === 'premium_tour' || productType === 'ultra_lux_tour' || productType === 'signature_series_tour' || productType === 'custom_tour' || productType === 'insider_series_tour'
}
export function isTourProductTypeLabelled(productType: App.Tours.V2ProductType): productType is Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2CustomProductType> {
  return productType === 'deluxe_tour' || productType === 'premium_tour' || productType === 'ultra_lux_tour' || productType === 'signature_series_tour' || productType === 'insider_series_tour'
}

interface TourVariationLabelParams {
  productType: App.Tours.V2ProductType
  packageOptionName?: string
}

export function getTourVariationLabel(params: TourVariationLabelParams): string {
  const { productType, packageOptionName } = params
  const hasExtension = tourOptionPackages.includes(packageOptionName ?? '')
  const isBase = (hasExtension && packageOptionName === TOUR_V2_PACKAGE_OPTION_BASE) || !hasExtension

  if (!hasExtension) {
    return ''
  }

  if (productType === 'custom_tour') {
    if (isBase) return 'Deluxe'
    if (packageOptionName === TOUR_V2_PACKAGE_OPTION_EXTENDED) return 'Superior'
    if (packageOptionName === TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE) return 'Lux Upgrade'
  }

  return isBase ? 'Base' : 'Extension'
}

export function shouldShowAdditionalTourWays(productType: App.Tours.V2ProductType): boolean {
  return productType !== 'custom_tour'
}

export function isTourLimitedTimeOffer(tour: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary): boolean {
  return tour.name?.includes('Limited Time Offer') ?? false
}

export function getStartEndPropertyId(tour: App.Tours.TourV2Offer, variationId: string): { startPropertyId: string | undefined, endPropertyId: string | undefined } {
  const recommandedHotel = tour.recommendedHotelAddOns?.[variationId]
  return {
    startPropertyId: recommandedHotel?.startBedbank?.[0] ? `bedbank:${recommandedHotel.startBedbank[0]}` : undefined,
    endPropertyId: recommandedHotel?.endBedbank?.[0] ? `bedbank:${recommandedHotel.endBedbank[0]}` : undefined,
  }
}

export function getMonthlyTourHomepageHeroImageId() {
  const date = new Date()
  const month = date.getMonth()
  const rotationIndex = month % TOUR_HERO_IMAGES_IDS_ROTATION.length
  return TOUR_HERO_IMAGES_IDS_ROTATION[rotationIndex]
}

export function getPrePostAccommodationStatus(order: App.Order): TourAccommodationBookingStatus {
  const bookingStatus = {
    preAccommodationBooked: false,
    postAccommodationBooked: false,
  }
  const tourItem = order.tourItems.find(tourItem => tourItem.status !== 'cancelled')
  if (!tourItem) return bookingStatus
  const bedBankItems = order.bedbankItems
  const tourStartDate = tourItem.tour.startDate
  const tourEndDate = tourItem.tour.endDate

  bedBankItems.forEach(bedBank => {
    const bedBankCheckIn = new Date(bedBank.checkIn.format('YYYY-MM-DD'))
    if (bedBankCheckIn <= new Date(tourStartDate)) {
      bookingStatus.preAccommodationBooked = true
    }
    if (bedBankCheckIn >= new Date(tourEndDate)) {
      bookingStatus.postAccommodationBooked = true
    }
  })
  return bookingStatus
}

export function getPrePostAccommodationDetails(order: App.Order): [
  preTourAccommodations: Array<App.OrderBedbankItem>,
  postTourAccommodations: Array<App.OrderBedbankItem>,
] {
  const tourItem = order.tourItems.find(tourItem => tourItem.status === 'completed')
  if (!tourItem) return [EmptyArray, EmptyArray]
  const bedBankItems = order.bedbankItems
  const tourStartDate = tourItem.tour.startDate
  const tourEndDate = tourItem.tour.endDate
  return [
    bedBankItems.filter((bedbank) => bedbank.checkIn.toDate() <= new Date(tourStartDate)),
    bedBankItems.filter((bedbank) => bedbank.checkIn.toDate() >= new Date(tourEndDate)),
  ]
}

export function isTourTravellerApiEnabled(): boolean {
  return config.USE_SVC_TOUR_TRAVELLER_API
}
